import React, { useContext, useState } from "react";
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
  keyframes,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Support as SupportIcon,
  MenuBook as GuideIcon,
  Settings as SettingsIcon,
  SmartToy as SmartToyIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { AccountContext } from "../Accounts";
import { getUsername, stringAvatar } from "../shared/helpers/helper";
import { tokens } from "../../theme/tokens";
import AIChatDrawer from "../AIChatbot/AIChatDrawer";

export const HEADER_HEIGHT = "56px";

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const Bar = styled("header")({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: HEADER_HEIGHT,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  gap: tokens.space[2],
  padding: `0 ${tokens.space[3]}`,
  backgroundColor: tokens.color.surface.default,
  borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  boxShadow: "none",
});

const focusRing = {
  "&:focus-visible": {
    outline: `${tokens.focus.width} solid ${tokens.color.focus.ring}`,
    outlineOffset: tokens.focus.offset,
  },
} as const;

interface HeaderProps {
  handleDrawerToggle: () => void;
  handleSettingsClick: () => void;
}

export default function Header({
  handleDrawerToggle,
  handleSettingsClick,
}: HeaderProps) {
  const { logout, avatarColor } = useContext(AccountContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [aiDrawerOpen, setAIDrawerOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const username = getUsername();

  return (
    <Bar>
      <IconButton
        aria-label="Toggle navigation"
        onClick={handleDrawerToggle}
        sx={{
          color: tokens.color.text.secondary,
          borderRadius: tokens.radius.sm,
          "&:hover": { backgroundColor: tokens.color.surface.hover },
          ...focusRing,
        }}
      >
        <MenuIcon />
      </IconButton>

      <Typography
        sx={{
          fontSize: tokens.type.subheading.size,
          fontWeight: tokens.type.subheading.weight,
          color: tokens.color.brand.primary,
          letterSpacing: "0.01em",
        }}
      >
        Occutrace
      </Typography>

      <Box sx={{ flexGrow: 1 }} />

      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        role="button"
        tabIndex={0}
        aria-label="Account menu"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setAnchorEl(e.currentTarget);
        }}
        sx={{
          display: "flex",
          padding: tokens.space[1],
          borderRadius: tokens.radius.full,
          cursor: "pointer",
          transition: `background-color ${tokens.motion.fast} ${tokens.motion.easing}`,
          "&:hover": { backgroundColor: tokens.color.surface.hover },
          ...focusRing,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: tokens.type.caption.size,
            backgroundColor: avatarColor,
          }}
        >
          {stringAvatar(username)}
        </Avatar>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: tokens.radius.md,
            border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ padding: `${tokens.space[2]} ${tokens.space[4]}` }}>
          <Typography
            sx={{
              fontSize: tokens.type.caption.size,
              color: tokens.color.text.secondary,
            }}
          >
            Signed in as
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.type.dense.size,
              color: tokens.color.text.primary,
              wordBreak: "break-all",
            }}
          >
            {username}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem disabled sx={{ opacity: 0.5 }}>
          <ListItemIcon>
            <SupportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Support</ListItemText>
        </MenuItem>
        <MenuItem disabled sx={{ opacity: 0.5 }}>
          <ListItemIcon>
            <GuideIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Guide</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleSettingsClick();
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setAIDrawerOpen(true);
          }}
        >
          <ListItemIcon
            sx={{ "&:hover .ai-icon": { animation: `${pulse} 1s infinite` } }}
          >
            <SmartToyIcon className="ai-icon" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create with AI</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={logout}
          sx={{ color: tokens.color.status.error }}
        >
          <ListItemIcon sx={{ color: tokens.color.status.error }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>

      <AIChatDrawer open={aiDrawerOpen} onClose={() => setAIDrawerOpen(false)} />
    </Bar>
  );
}
